import Dexie, { Table } from 'dexie';

export interface OfflineReport {
  id: string;
  projectId: string;
  projectName: string;
  contractorId: string;
  week: string;
  weekNumber: number;
  year: number;
  startDate: string;
  endDate: string;
  progress: number;
  description: string;
  photos: any[];
  status: 'pending' | 'synced' | 'failed';
  syncAttempts: number;
  createdAt: number;
  updatedAt: number;
  lastSyncError?: string;
  metadata?: Record<string, any>;
}

export interface OfflinePhoto {
  id: string;
  reportId: string;
  data: string;
  thumbnail?: string;
  caption?: string;
  order: number;
  uploadedAt: number;
  synced: boolean;
  uploadError?: string;
}

export interface OfflineProject {
  id: string;
  name: string;
  clientId: string;
  clientName?: string;
  status: string;
  progress: number;
  address?: string;
  budget: number;
  lastSyncAt: number;
  cachedAt: number;
  metadata?: Record<string, any>;
}

export interface OfflineMessage {
  id: string;
  projectId: string;
  projectName?: string;
  senderId: string;
  senderName?: string;
  receiverId?: string;
  receiverName?: string;
  content: string;
  type: 'text' | 'observation' | 'change_request';
  status: 'pending' | 'synced' | 'failed';
  createdAt: number;
  syncAttempts: number;
  lastSyncError?: string;
  readAt?: number;
  updatedAt?: number;
}

export interface OfflineChangeOrder {
  id: string;
  projectId: string;
  projectName: string;
  description: string;
  items: any[];
  reason: string;
  calculatedImpact?: number;
  originalBudget?: number;
  newBudget?: number;
  status: 'draft' | 'pending' | 'synced' | 'failed';
  syncAttempts: number;
  createdAt: number;
  updatedAt: number;
  lastSyncError?: string;
}

export interface OfflineInvoice {
  id: string;
  projectId: string;
  projectName?: string;
  number: string;
  amount: number;
  tax: number;
  total: number;
  concept: string;
  status: 'pending' | 'paid' | 'cancelled' | 'overdue';
  dueDate: number;
  issuedDate: number;
  paidDate?: number;
  syncStatus: 'pending' | 'synced' | 'failed';
  syncAttempts: number;
  createdAt: number;
  updatedAt: number;
  lastSyncError?: string;
}

export interface OfflinePermit {
  id: string;
  projectId: string;
  projectName?: string;
  permitNumber: string;
  issuedBy: string;
  issuedDate: number;
  expiryDate: number;
  status: 'pending' | 'issued' | 'expired' | 'rejected' | 'in_review';
  documents: string[];
  notes?: string;
  syncStatus: 'pending' | 'synced' | 'failed';
  syncAttempts: number;
  createdAt: number;
  updatedAt: number;
  lastSyncError?: string;
}

export interface OfflineConversation {
  id: string;
  projectId: string;
  projectName: string;
  participant1Id: string;
  participant1Name?: string;
  participant2Id: string;
  participant2Name?: string;
  lastMessagePreview?: string;
  lastMessageAt?: number;
  unreadCount: number;
  status: 'active' | 'archived';
  createdAt: number;
  updatedAt: number;
}

export class GranArquiOfflineDB extends Dexie {
  reports!: Table<OfflineReport, string>;
  photos!: Table<OfflinePhoto, string>;
  projects!: Table<OfflineProject, string>;
  messages!: Table<OfflineMessage, string>;
  changeOrders!: Table<OfflineChangeOrder, string>;
  invoices!: Table<OfflineInvoice, string>;
  permits!: Table<OfflinePermit, string>;
  conversations!: Table<OfflineConversation, string>;
  offlineQueue!: Table<any, string>;

  constructor() {
    super('GranArquiOfflineDB');

    this.version(1).stores({
      reports: 'id, projectId, contractorId, status, createdAt, updatedAt',
      photos: 'id, reportId, synced, uploadedAt',
      projects: 'id, clientId, status, lastSyncAt',
      messages: 'id, projectId, senderId, status, createdAt',
      changeOrders: 'id, projectId, status, createdAt, updatedAt',
      invoices: 'id, projectId, syncStatus, createdAt',
      permits: 'id, projectId, syncStatus, createdAt',
      conversations: 'id, projectId, participant1Id, participant2Id, status, updatedAt',
    });

    this.version(2).stores({
      reports: 'id, projectId, contractorId, status, createdAt, updatedAt',
      photos: 'id, reportId, synced, uploadedAt',
      projects: 'id, clientId, status, lastSyncAt',
      messages: 'id, projectId, senderId, status, createdAt',
      changeOrders: 'id, projectId, status, createdAt, updatedAt',
      invoices: 'id, projectId, syncStatus, createdAt',
      permits: 'id, projectId, syncStatus, createdAt',
      conversations: 'id, projectId, participant1Id, participant2Id, status, updatedAt',
      offlineQueue: 'id, entity, action, status, localRef, createdAt, updatedAt',
    });
  }
}

export const db = new GranArquiOfflineDB();

export const OfflineAdvanceUtils = {
  async saveReport(
    report: Omit<OfflineReport, 'id' | 'status' | 'syncAttempts' | 'createdAt' | 'updatedAt'>
  ): Promise<string> {
    const id = crypto.randomUUID();
    const now = Date.now();
    await db.reports.add({
      ...report,
      id,
      status: 'pending',
      syncAttempts: 0,
      createdAt: now,
      updatedAt: now,
    });
    return id;
  },
  async getPendingReports(): Promise<OfflineReport[]> {
    return db.reports.where('status').anyOf(['pending', 'failed']).toArray();
  },
  async getReportsByProject(projectId: string): Promise<OfflineReport[]> {
    return db.reports.where('projectId').equals(projectId).toArray();
  },
  async updateReportStatus(
    id: string,
    status: OfflineReport['status'],
    error?: string
  ): Promise<void> {
    await db.reports.update(id, { status, updatedAt: Date.now(), lastSyncError: error });
  },
  async deleteReport(id: string): Promise<void> {
    const report = await db.reports.get(id);
    if (report) {
      await db.photos.where('reportId').equals(id).delete();
      await db.reports.delete(id);
    }
  },
  async getReport(id: string): Promise<OfflineReport | undefined> {
    return db.reports.get(id);
  },
  async getPendingCount(): Promise<number> {
    return db.reports.where('status').anyOf(['pending', 'failed']).count();
  },
  async savePhoto(photo: Omit<OfflinePhoto, 'id'>): Promise<string> {
    const id = crypto.randomUUID();
    await db.photos.add({ ...photo, id });
    return id;
  },
  async getPhotosByReport(reportId: string): Promise<OfflinePhoto[]> {
    return db.photos.where('reportId').equals(reportId).sortBy('order');
  },
  async markPhotosSynced(reportId: string): Promise<void> {
    await db.photos.where('reportId').equals(reportId).modify({ synced: true });
  },
};

export const OfflineMessageUtils = {
  async saveMessage(
    message: Omit<OfflineMessage, 'id' | 'status' | 'syncAttempts' | 'createdAt' | 'updatedAt'>
  ): Promise<string> {
    const id = crypto.randomUUID();
    const now = Date.now();
    await db.messages.add({
      ...message,
      id,
      status: 'pending',
      syncAttempts: 0,
      createdAt: now,
      updatedAt: now,
    });
    return id;
  },
  async getPendingMessages(): Promise<OfflineMessage[]> {
    return db.messages.where('status').anyOf(['pending', 'failed']).toArray();
  },
  async getMessagesByProject(projectId: string): Promise<OfflineMessage[]> {
    return db.messages.where('projectId').equals(projectId).sortBy('createdAt');
  },
  async getMessagesBySender(senderId: string): Promise<OfflineMessage[]> {
    return db.messages.where('senderId').equals(senderId).toArray();
  },
  async updateMessageStatus(
    id: string,
    status: OfflineMessage['status'],
    error?: string
  ): Promise<void> {
    await db.messages.update(id, { status, lastSyncError: error });
  },
  async deleteMessage(id: string): Promise<void> {
    await db.messages.delete(id);
  },
  async getUnreadCount(userId: string): Promise<number> {
    return db.messages
      .where('receiverId')
      .equals(userId)
      .and((msg) => msg.status === 'synced' && !msg.readAt)
      .count();
  },
};

export const OfflineChangeOrderUtils = {
  async saveChangeOrder(
    order: Omit<OfflineChangeOrder, 'id' | 'status' | 'syncAttempts' | 'createdAt' | 'updatedAt'>
  ): Promise<string> {
    const id = crypto.randomUUID();
    const now = Date.now();
    await db.changeOrders.add({
      ...order,
      id,
      status: 'pending',
      syncAttempts: 0,
      createdAt: now,
      updatedAt: now,
    });
    return id;
  },
  async getPendingChangeOrders(): Promise<OfflineChangeOrder[]> {
    return db.changeOrders.where('status').anyOf(['pending', 'failed']).toArray();
  },
  async getChangeOrdersByProject(projectId: string): Promise<OfflineChangeOrder[]> {
    return db.changeOrders.where('projectId').equals(projectId).toArray();
  },
  async updateChangeOrderStatus(
    id: string,
    status: OfflineChangeOrder['status'],
    error?: string
  ): Promise<void> {
    await db.changeOrders.update(id, { status, updatedAt: Date.now(), lastSyncError: error });
  },
  async updateChangeOrder(id: string, data: Partial<OfflineChangeOrder>): Promise<void> {
    await db.changeOrders.update(id, { ...data, updatedAt: Date.now() });
  },
  async deleteChangeOrder(id: string): Promise<void> {
    await db.changeOrders.delete(id);
  },
  async getDraftChangeOrders(): Promise<OfflineChangeOrder[]> {
    return db.changeOrders.where('status').equals('draft').toArray();
  },
};

export const OfflineConversationUtils = {
  async saveConversation(
    conv: Omit<OfflineConversation, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<string> {
    const id = crypto.randomUUID();
    const now = Date.now();
    await db.conversations.add({ ...conv, id, createdAt: now, updatedAt: now });
    return id;
  },
  async getConversationsByUser(userId: string): Promise<OfflineConversation[]> {
    return db.conversations
      .where('participant1Id')
      .equals(userId)
      .or('participant2Id')
      .equals(userId)
      .toArray();
  },
  async getConversation(
    projectId: string,
    userId1: string,
    userId2: string
  ): Promise<OfflineConversation | undefined> {
    return db.conversations
      .where('projectId')
      .equals(projectId)
      .and(
        (c) =>
          (c.participant1Id === userId1 && c.participant2Id === userId2) ||
          (c.participant1Id === userId2 && c.participant2Id === userId1)
      )
      .first();
  },
  async updateConversation(id: string, data: Partial<OfflineConversation>): Promise<void> {
    await db.conversations.update(id, { ...data, updatedAt: Date.now() });
  },
  async deleteConversation(id: string): Promise<void> {
    await db.conversations.delete(id);
  },
  async markMessagesAsRead(conversationId: string, userId: string): Promise<void> {
    const conv = await db.conversations.get(conversationId);
    if (conv) {
      await db.conversations.update(conversationId, { unreadCount: 0, updatedAt: Date.now() });
      await db.messages
        .where('receiverId')
        .equals(userId)
        .and((msg) => msg.status === 'synced' && !msg.readAt)
        .modify({ readAt: Date.now() });
    }
  },
};

export const OfflineInvoiceUtils = {
  async saveInvoice(
    invoice: Omit<OfflineInvoice, 'id' | 'syncStatus' | 'syncAttempts' | 'createdAt' | 'updatedAt'>
  ): Promise<string> {
    const id = crypto.randomUUID();
    const now = Date.now();
    await db.invoices.add({
      ...invoice,
      id,
      syncStatus: 'pending',
      syncAttempts: 0,
      createdAt: now,
      updatedAt: now,
    });
    return id;
  },
  async getPendingInvoices(): Promise<OfflineInvoice[]> {
    return db.invoices.where('syncStatus').anyOf(['pending', 'failed']).toArray();
  },
  async getInvoicesByProject(projectId: string): Promise<OfflineInvoice[]> {
    return db.invoices.where('projectId').equals(projectId).toArray();
  },
};

export const OfflinePermitUtils = {
  async savePermit(
    permit: Omit<OfflinePermit, 'id' | 'syncStatus' | 'syncAttempts' | 'createdAt' | 'updatedAt'>
  ): Promise<string> {
    const id = crypto.randomUUID();
    const now = Date.now();
    await db.permits.add({
      ...permit,
      id,
      syncStatus: 'pending',
      syncAttempts: 0,
      createdAt: now,
      updatedAt: now,
    });
    return id;
  },
  async getPendingPermits(): Promise<OfflinePermit[]> {
    return db.permits.where('syncStatus').anyOf(['pending', 'failed']).toArray();
  },
  async getPermitsByProject(projectId: string): Promise<OfflinePermit[]> {
    return db.permits.where('projectId').equals(projectId).toArray();
  },
};

export async function clearAllOfflineData(): Promise<void> {
  await Promise.all([
    db.reports.clear(),
    db.photos.clear(),
    db.projects.clear(),
    db.messages.clear(),
    db.changeOrders.clear(),
    db.invoices.clear(),
    db.permits.clear(),
    db.conversations.clear(),
    db.offlineQueue.clear(),
  ]);
}

export async function getOfflineStats(): Promise<{
  reports: number;
  photos: number;
  projects: number;
  messages: number;
  changeOrders: number;
  invoices: number;
  permits: number;
  conversations: number;
  pendingSync: number;
}> {
  const [
    reports,
    photos,
    projects,
    messages,
    changeOrders,
    invoices,
    permits,
    conversations,
  ] = await Promise.all([
    db.reports.count(),
    db.photos.count(),
    db.projects.count(),
    db.messages.count(),
    db.changeOrders.count(),
    db.invoices.count(),
    db.permits.count(),
    db.conversations.count(),
  ]);

  const pendingSync = await Promise.all([
    db.reports.where('status').notEqual('synced').count(),
    db.messages.where('status').notEqual('synced').count(),
    db.changeOrders.where('status').notEqual('synced').count(),
    db.invoices.where('syncStatus').notEqual('synced').count(),
    db.permits.where('syncStatus').notEqual('synced').count(),
  ]).then((results) => results.reduce((a, b) => a + b, 0));

  return {
    reports,
    photos,
    projects,
    messages,
    changeOrders,
    invoices,
    permits,
    conversations,
    pendingSync,
  };
}